/**
 * AI Execution Store
 *
 * Handles execution requests from MCP tools.
 * QueryEditorTab listens to this store and triggers execution when requested.
 */

import { create } from 'zustand';

/** Execution request from MCP */
export type AiExecutionRequest = {
  requestId: string;
  tabId: string;
  /** Execute only selected text */
  selectedOnly?: boolean;
  /** Timestamp of request */
  requestedAt: number;
  /**
   * Resolve only once execution has actually finished, instead of the moment
   * it starts. Used by the agent execute-with-confirm protocol, which must
   * report a real outcome back to the agent. Absent (the MCP path) keeps the
   * original resolve-on-start behaviour.
   */
  awaitCompletion?: boolean;
  /**
   * The exact SQL the user was shown when they consented, captured at prompt
   * time. When set, the tab-side runner executes **only** if the live editor
   * buffer still holds this text; otherwise it refuses with
   * `AI_EXECUTION_SQL_CHANGED` and nothing runs.
   *
   * This closes the consent-bypass window: the prompt renders the tab content
   * as it was when the request arrived, while execution reads the buffer at
   * click time — and an agent can rewrite `tabs/tab_<id>.sql` in between, so
   * that the user approves `SELECT 1` and `DELETE FROM users` runs.
   *
   * Optional only for the terminal protocol, which omits it when no approved
   * SQL was captured (`useAiExecutionRequests.ts`). The MCP path **always**
   * sets it (`useMcpUiControl.ts`), using `''` when nothing is authorised —
   * which refuses everything but an empty buffer. Do not read the optionality
   * as a sanctioned unbound mode: an absent expectation is exactly the shape
   * that produced two separate consent bypasses on the MCP side, and the fix
   * there was to make the authorisation required rather than optional. Making
   * it required here too is wanted; it is a typed change with test impact, not
   * a comment edit.
   */
  expectedSql?: string;
  /**
   * The connection and database the user was shown when they consented,
   * captured at the same moment as `expectedSql`. The tab-side runner refuses
   * if the tab has since been rebound.
   *
   * Same class of bug as `expectedSql`, on the target side: the grant and the
   * prompt are both evaluated against the tab's connection at request time,
   * while execution reads it live — so a rebind landing in between would send
   * an approved statement to a database the user never agreed to.
   */
  expectedTarget?: { connectionId?: string; database?: string };
};

/**
 * Refusal code emitted when `expectedSql` no longer matches the editor buffer.
 * A code rather than prose: the terminal-side protocol maps it to its own
 * app-authored copy for the user and for the agent's response file.
 */
export const AI_EXECUTION_SQL_CHANGED = 'sql-changed';

/**
 * Refusal code emitted when `expectedTarget` no longer matches the tab's live
 * connection or database. Same contract as `AI_EXECUTION_SQL_CHANGED`.
 */
export const AI_EXECUTION_TARGET_CHANGED = 'target-changed';

/**
 * Refusal code emitted when the user cancels the destructive-query dialog.
 * A human saying no is not a failed run, and must not be reported to the agent
 * as one — the third instance of that same untruth in this protocol.
 */
export const AI_EXECUTION_NOT_CONFIRMED = 'not-confirmed';

/** Execution result to return to MCP */
export type AiExecutionResult = {
  success: boolean;
  executionId?: string;
  error?: string;
};

/** Pending request with resolve callback */
type PendingRequest = {
  request: AiExecutionRequest;
  resolve: (result: AiExecutionResult) => void;
};

type AiExecutionState = {
  /** Map of tabId -> pending request */
  pendingRequests: Map<string, PendingRequest>;

  /** Add an execution request and return a promise for the result */
  addRequest: (request: AiExecutionRequest) => Promise<AiExecutionResult>;

  /** Get pending request for a tab */
  getRequest: (tabId: string) => AiExecutionRequest | undefined;

  /** Clear request for a tab (called when execution starts) */
  resolveRequest: (tabId: string, result: AiExecutionResult) => void;
};

export const useAiExecutionStore = create<AiExecutionState>((set, get) => ({
  pendingRequests: new Map(),

  addRequest: (request) => {
    return new Promise((resolve) => {
      set((state) => {
        const newMap = new Map(state.pendingRequests);
        // If there's already a pending request for this tab, reject it
        const existing = newMap.get(request.tabId);
        if (existing) {
          existing.resolve({ success: false, error: 'Superseded by new request' });
        }
        newMap.set(request.tabId, { request, resolve });
        return { pendingRequests: newMap };
      });
    });
  },

  getRequest: (tabId) => {
    return get().pendingRequests.get(tabId)?.request;
  },

  resolveRequest: (tabId, result) => {
    set((state) => {
      const pending = state.pendingRequests.get(tabId);
      if (pending) {
        pending.resolve(result);
        const newMap = new Map(state.pendingRequests);
        newMap.delete(tabId);
        return { pendingRequests: newMap };
      }
      return state;
    });
  },
}));
